import React, { useCallback, useMemo, MouseEventHandler } from 'react'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import Group from 'components/utils/Group'
import { connect } from 'react-redux'
import useBusinessTravellerEmployeeQuery from 'businessTraveller/hooks/useEmployeeQuery'
import { sortBy } from 'lib/array/arrayUtils'
import CheckboxButton from 'LuxKit/Checkbox/CheckboxButton'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItem from 'LuxKit/List/ListItem'

interface MappedStateProps {
  businessAccount?: App.BusinessTraveller.CurrentBusinessAccount
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
}

interface Props {
  inputActive: boolean
  searchTerm?: string
  onAddEmployee: (employeeId: string) => void
  onRemoveEmployee: (employeeId: string) => void
}

function BusinessTravellerSelectHotelsTravellersContent(props: Props & MappedStateProps) {
  const { inputActive, searchTerm = '', travellerEmployees, onAddEmployee, onRemoveEmployee } = props
  const { employees } = useBusinessTravellerEmployeeQuery()

  const searchActive = !!searchTerm

  const filteredEmployeeOptions = useMemo<Array<App.BusinessTraveller.EmployeeFromMeEndpoint>>(() => {
    const employeeOptions = sortBy(Object.values(employees ?? {}), (employee) => employee.firstName + employee.lastName, 'asc')
    if (employeeOptions.length && searchTerm) {
      return employeeOptions.filter((employee) => {
        return employee.firstName.toLocaleLowerCase().startsWith(searchTerm.toLocaleLowerCase()) ||
          employee.lastName.toLocaleLowerCase().startsWith(searchTerm.toLocaleLowerCase())
      })
    } else {
      return employeeOptions
    }
  }, [employees, searchTerm])

  const handleEmployeeClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const employeeId = event.currentTarget.dataset.employeeid
    if (employeeId) {
      if (travellerEmployees.some(employee => employee.id === employeeId)) {
        onRemoveEmployee(employeeId)
      } else {
        onAddEmployee(employeeId)
      }
    }
  }, [travellerEmployees, onAddEmployee, onRemoveEmployee])

  const showEmployees = inputActive && filteredEmployeeOptions.length > 0
  const showNoResults = inputActive && searchActive && !showEmployees

  return (
    <Group direction="vertical" gap={8}>
      {showEmployees && (
        <Group direction="vertical" gap={4}>
          {filteredEmployeeOptions.map((employeeOption) => (
            <ClickableListItem
              key={employeeOption.id}
              size="large"
              startIcon={<AvatarCircle
                firstName={employeeOption.firstName}
                lastName={employeeOption.lastName}
              />}
              title={`${employeeOption.firstName} ${employeeOption.lastName}`}
              subtitle={employeeOption.email}
              data-employeeid={employeeOption.id}
              onClick={handleEmployeeClick}
              endIcon={<CheckboxButton checked={travellerEmployees.some(employee => employee.id === employeeOption.id)} />}
            />
          ))}
        </Group>
      )}
      {showNoResults && <ListItem titleColour="neutral-two" title={`No results found for "${searchTerm}"`} />}
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    businessAccount: state.businessTraveller.currentBusinessAccount,
    travellerEmployees: selectSelectedTravellerEmployees(state),
  }
})(BusinessTravellerSelectHotelsTravellersContent)
